import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { usePagePermission } from "@/hooks/usePagePermission";
import { usePermissionCodeForPath } from "@/hooks/usePermissionCodeForPath";
import { isFrontendRbacDisabled } from "@/utils/rbacToggle";
import {
  shouldBypassPermissionMatrix,
  getEffectiveUser,
  isSalesmanRole,
} from "@/utils/permissionsUser";

/**
 * Hides navigation when user lacks can_view for the resolved permission code
 * (from /permission-pages/active `category` vs `to`).
 * `unrestricted`: always show (e.g. dashboard, profile) for matrix users.
 */
export default function NavPermissionLink({
  permissionCode: permissionCodeProp,
  unrestricted = false,
  to,
  children,
  ...rest
}) {
  const rbacDisabled = isFrontendRbacDisabled();
  const authUser = useSelector((s) => s.auth.user);
  const bypass = useSelector((s) => s.permissions.bypass);
  const activePagesStatus = useSelector((s) => s.permissions.activePagesStatus);
  const effectiveUser = getEffectiveUser(authUser);
  const bypassesMatrix = shouldBypassPermissionMatrix(effectiveUser);

  const resolvedCode = usePermissionCodeForPath(to);
  const permissionCode = permissionCodeProp ?? resolvedCode;

  const { allowed, ready } = usePagePermission(permissionCode, "view");

  if (rbacDisabled || !isSalesmanRole(effectiveUser)) {
    return (
      <Link to={to} {...rest}>
        {children}
      </Link>
    );
  }

  if (bypassesMatrix || bypass) {
    return (
      <Link to={to} {...rest}>
        {children}
      </Link>
    );
  }

  if (unrestricted) {
    return (
      <Link to={to} {...rest}>
        {children}
      </Link>
    );
  }

  if (activePagesStatus === "loading" || activePagesStatus === "idle") {
    return null;
  }

  if (activePagesStatus === "succeeded" && !permissionCode) {
    return null;
  }

  if (!permissionCode) {
    return null;
  }

  if (!ready) return null;
  if (!allowed) return null;
  return (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
}

NavPermissionLink.propTypes = {
  permissionCode: PropTypes.string,
  unrestricted: PropTypes.bool,
  to: PropTypes.string.isRequired,
  children: PropTypes.node,
};
